/**
 * Agent-console WebSocket manager — one socket per tab, shared by every
 * consumer through LiveAgentProvider.
 *
 * A module-level singleton with refcounting rather than a per-hook socket:
 * several components need the same stream, and CRA Fast Refresh re-runs effects
 * on every save, which would otherwise churn connections constantly.
 */

import { buildAgentWsUrl, refreshAccessTokenOnce } from "../../../API/api";
import { GetCookies } from "../../../utils/GetCookies";
import { MockLiveAgentSocket, isMockEnabled } from "./mockLiveAgentServer";

const PING_MS = 30_000;
/** No inbound frame for this long means the socket is half-open, however healthy it looks. */
const WATCHDOG_MS = 90_000;
const MAX_BACKOFF_MS = 30_000;
/** Refresh this long before the JWT expires, so rotation never races a 4401. */
const ROTATE_LEAD_MS = 90_000;
/** Fallback when the token can't be decoded; access tokens live one hour. */
const ROTATE_FALLBACK_MS = 50 * 60_000;
/** Grace period before actually closing, so a remount is a no-op. */
const RELEASE_GRACE_MS = 5_000;

let socket = null;
let status = "idle";
let attempt = 0;
let refCount = 0;

let pingTimer = null;
let backoffTimer = null;
let rotateTimer = null;
let releaseTimer = null;

let lastFrameAt = 0;
let tokenFingerprint = null;
let refreshedForToken = null;
/** Frames queued while disconnected, flushed on live_list. */
let outbox = [];
const listeners = new Set();

function emit(event) {
  for (const fn of listeners) fn(event);
}

function setStatus(next) {
  if (status === next) return;
  status = next;
  emit({ kind: "status", status: next, attempt });
}

// ── connection ──────────────────────────────────────────────────────────────

async function connect() {
  if (socket || status === "forbidden") return;

  let { accessToken } = GetCookies();
  // The cookie expires after an hour while the app stays mounted, so this is
  // routinely undefined. Refresh before building the URL — connecting with
  // ?token=undefined just produces an instant 4401 loop.
  if (!accessToken && !isMockEnabled()) {
    await refreshAccessTokenOnce();
    accessToken = GetCookies().accessToken;
  }

  setStatus("connecting");
  tokenFingerprint = accessToken ? accessToken.slice(-8) : null;

  const Transport = isMockEnabled() ? MockLiveAgentSocket : WebSocket;
  let ws;
  try {
    ws = new Transport(buildAgentWsUrl(accessToken));
  } catch {
    scheduleReconnect();
    return;
  }
  socket = ws;

  ws.onopen = () => {
    lastFrameAt = Date.now();
    startTimers();
  };

  ws.onmessage = (event) => {
    lastFrameAt = Date.now();
    let frame;
    try {
      frame = JSON.parse(event.data);
    } catch {
      return;
    }
    if (frame.type === "pong") return;

    // Only a clean live_list proves the connection is actually usable — an
    // auth rejection arrives *after* onopen, so resetting there would clear
    // the backoff on a socket that is about to be refused.
    if (frame.type === "live_list") {
      attempt = 0;
      setStatus("open");
      flushOutbox();
      scheduleRotation();
    }
    emit({ kind: "frame", frame });
  };

  ws.onerror = () => {};

  ws.onclose = (event) => {
    socket = null;
    stopTimers();
    routeClose(event?.code ?? 1006);
  };
}

function routeClose(code) {
  emit({ kind: "closed", code });

  if (code === 4403) {
    setStatus("forbidden");
    return;
  }

  if (code === 4401) {
    // If refreshing already failed to change the token, stop — otherwise this
    // becomes an endless mint/reject spin against the auth endpoint.
    if (refreshedForToken === tokenFingerprint) {
      setStatus("failed");
      return;
    }
    refreshedForToken = tokenFingerprint;
    refreshAccessTokenOnce().then(() => {
      attempt = 0;
      connect();
    });
    return;
  }

  if (code === 1000 && refCount === 0) {
    setStatus("idle");
    return;
  }

  scheduleReconnect();
}

function scheduleReconnect() {
  if (refCount === 0) return setStatus("idle");
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    // Nothing will succeed until we're back online; park and wait for the event.
    return setStatus("offline");
  }

  setStatus("reconnecting");
  const delay = Math.random() * Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attempt);
  attempt += 1;
  clearTimeout(backoffTimer);
  backoffTimer = setTimeout(connect, delay);
}

function reconnectNow() {
  if (socket || status === "forbidden") return;
  clearTimeout(backoffTimer);
  attempt = 0;
  connect();
}

// ── timers ──────────────────────────────────────────────────────────────────

function startTimers() {
  stopTimers();
  pingTimer = setInterval(() => {
    send({ type: "ping" });
    if (Date.now() - lastFrameAt > WATCHDOG_MS) {
      // Looks open, isn't. Force the close so the reconnect path runs.
      try {
        socket?.close(4000);
      } catch {
        /* already gone */
      }
    }
  }, PING_MS);
}

function stopTimers() {
  clearInterval(pingTimer);
  pingTimer = null;
}

/**
 * Proactive token rotation. Without it every operator takes a visible drop on
 * the hour, mid-conversation.
 */
function scheduleRotation() {
  clearTimeout(rotateTimer);
  if (isMockEnabled()) return;

  const { accessToken } = GetCookies();
  const expiresAt = decodeExp(accessToken);
  const delay = expiresAt
    ? Math.max(10_000, expiresAt - Date.now() - ROTATE_LEAD_MS)
    : ROTATE_FALLBACK_MS;

  rotateTimer = setTimeout(async () => {
    // Nobody is watching a hidden tab with nothing subscribed; rotate later.
    if (typeof document !== "undefined" && document.hidden)
      return scheduleRotation();
    await refreshAccessTokenOnce();
    // No {type:'auth'} frame in the contract yet, so reconnect with the new
    // token. State lives in the store, so this is invisible apart from a blip.
    try {
      socket?.close(1000);
    } catch {
      /* noop */
    }
    attempt = 0;
    connect();
  }, delay);
}

/** Minimal base64url JWT `exp` read; any failure falls back to a fixed timer. */
function decodeExp(token) {
  try {
    const payload = token.split(".")[1];
    const json = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
    );
    return typeof json.exp === "number" ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * The logged-in agent's user id, as a string, for comparing against a `mode`
 * frame's `handling_agent_id` so a console knows which conversations *it* is
 * handling (vs. another agent). Decoded from the access-token JWT's `user_id`
 * claim (SimpleJWT), which the backend broadcasts verbatim as `handling_agent_id`.
 * In mock mode returns "1" to match mockLiveAgentServer's "You" takeover id.
 * Returns null when it can't be determined (falls back to optimistic ownership).
 */
export function getCurrentUserId() {
  if (isMockEnabled()) return "1";
  try {
    const token = GetCookies().accessToken;
    if (!token) return null;
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return payload.user_id != null ? String(payload.user_id) : null;
  } catch {
    return null;
  }
}

// ── sending ─────────────────────────────────────────────────────────────────

function send(frame) {
  const OPEN = isMockEnabled() ? MockLiveAgentSocket.OPEN : WebSocket.OPEN;
  if (socket && socket.readyState === OPEN) {
    socket.send(JSON.stringify(frame));
    return true;
  }
  // Buffer everything except heartbeats: a subscribe fired during a reconnect
  // gap would otherwise vanish and the panel would sit empty forever.
  if (frame.type !== "ping") outbox.push(frame);
  return false;
}

function flushOutbox() {
  const queued = outbox;
  outbox = [];
  for (const frame of queued) send(frame);
}

// ── environment ─────────────────────────────────────────────────────────────

function onOnline() {
  reconnectNow();
}
function onVisibility() {
  if (document.visibilityState === "visible") reconnectNow();
}

// ── public API ──────────────────────────────────────────────────────────────

export const liveAgentSocket = {
  subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  /** Ref-counted so several components can hold the same connection open. */
  acquire() {
    refCount += 1;
    clearTimeout(releaseTimer);
    releaseTimer = null;
    if (!socket && status !== "forbidden") connect();

    if (refCount === 1 && typeof window !== "undefined") {
      window.addEventListener("online", onOnline);
      document.addEventListener("visibilitychange", onVisibility);
    }
  },

  release() {
    refCount = Math.max(0, refCount - 1);
    if (refCount > 0) return;

    // Grace period: a Fast Refresh or a remount resolves to a no-op instead of
    // tearing down and re-establishing the connection.
    clearTimeout(releaseTimer);
    releaseTimer = setTimeout(() => {
      if (refCount > 0) return;
      if (typeof window !== "undefined") {
        window.removeEventListener("online", onOnline);
        document.removeEventListener("visibilitychange", onVisibility);
      }
      clearTimeout(backoffTimer);
      clearTimeout(rotateTimer);
      stopTimers();
      outbox = [];
      try {
        socket?.close(1000);
      } catch {
        /* noop */
      }
      socket = null;
      setStatus("idle");
    }, RELEASE_GRACE_MS);
  },

  send,
  reconnectNow,
  getStatus: () => status,
  subscribeSession: (sessionId) =>
    send({ type: "subscribe", session_id: sessionId }),
  unsubscribeSession: (sessionId) =>
    send({ type: "unsubscribe", session_id: sessionId }),
  sendAgentMessage: (sessionId, text, clientMsgId) =>
    send({
      type: "agent_message",
      session_id: sessionId,
      text,
      client_msg_id: clientMsgId,
    }),
  // Best-effort "agent is composing" ping. Never queued (a stale typing signal is
  // useless) — the backend resets the 60s auto-revert clock and shows the widget an
  // "agent is responding" indicator. Throttling lives in the provider.
  sendAgentTyping: (sessionId) => {
    const OPEN = isMockEnabled() ? MockLiveAgentSocket.OPEN : WebSocket.OPEN;
    if (socket && socket.readyState === OPEN) {
      socket.send(JSON.stringify({ type: "typing", session_id: sessionId }));
    }
  },

  // Periodic authoritative re-sync (self-heal). Asks the backend to re-send live_list, which
  // the store reconciles like it does on connect — so a session whose session_close frame was
  // missed (socket blip / dropped frame / old widget that never closed cleanly) stops showing
  // as "live" within the refresh interval instead of lingering until the next reconnect.
  // OPEN-only, never queued: while disconnected the reconnect already re-sends live_list.
  requestList: () => {
    const OPEN = isMockEnabled() ? MockLiveAgentSocket.OPEN : WebSocket.OPEN;
    if (socket && socket.readyState === OPEN) {
      socket.send(JSON.stringify({ type: "list" }));
    }
  },

  // Agent-to-agent handoff (WS2). Requesting control of a chat another online agent holds
  // asks them first; accept/reject/cancel resolve it. (Taking over from the AI stays the
  // direct pause path in liveApi.)
  requestTakeover: (sessionId) =>
    send({ type: "takeover_request", session_id: sessionId }),
  respondTakeover: (sessionId, accept) =>
    send({ type: "takeover_response", session_id: sessionId, accept }),
  cancelTakeover: (sessionId) =>
    send({ type: "takeover_cancel", session_id: sessionId }),
};
